if (!customElements.get('cart-invoice-form')) {
  customElements.define(
    'cart-invoice-form',
    class CartInvoiceForm extends HTMLElement {
      constructor() {
        super();
        this.saveTimer = null;
        this.saveQueue = Promise.resolve();
        this.changeVersion = 0;
        this.savedVersion = 0;
      }

      connectedCallback() {
        this.form = this.closest('form');
        this.toggle = this.querySelector('[data-invoice-toggle]');
        this.uncheckedValue = this.querySelector('[data-invoice-unchecked-value]');
        this.fieldsPanel = this.querySelector('[data-invoice-fields]');
        this.errorSummary = this.querySelector('[data-invoice-error-summary]');
        this.saveStatus = this.querySelector('[data-invoice-save-status]');

        if (!this.form || !this.toggle || !this.fieldsPanel) return;

        this.form.addEventListener('submit', (event) => this.onSubmit(event));
        this.toggle.addEventListener('change', () => this.onToggle());
        this.addEventListener('input', (event) => this.onInput(event));
        this.addEventListener('change', (event) => this.onChange(event));
        this.addEventListener('focusout', (event) => this.onFocusOut(event));

        this.setExpanded(this.toggle.checked, false);
        this.setupAcceleratedCheckoutGuard();
      }

      onToggle() {
        this.setExpanded(this.toggle.checked, true);
        this.markDirty();
        this.saveAttributes(true);
      }

      setExpanded(expanded, moveFocus) {
        this.toggle.setAttribute('aria-expanded', String(expanded));
        this.uncheckedValue.disabled = expanded;
        this.fieldsPanel.classList.toggle('is-open', expanded);
        this.fieldsPanel.setAttribute('aria-hidden', String(!expanded));
        this.fieldsPanel.inert = !expanded;

        this.querySelectorAll('[data-invoice-required]').forEach((input) => {
          input.required = expanded;
          input.setAttribute('aria-required', String(expanded));
        });

        if (!expanded) {
          this.clearAllErrors();
        } else if (moveFocus) {
          window.setTimeout(() => {
            this.querySelector('[data-invoice-required]')?.focus({ preventScroll: true });
          }, 310);
        }
      }

      onInput(event) {
        if (!(event.target instanceof HTMLInputElement) || event.target === this.toggle) return;

        if (event.target.matches('[data-invoice-tax-id]')) {
          event.target.value = event.target.value.replace(/[^0-9 -]/g, '');
        }

        if (event.target.matches('[data-invoice-postal-code]')) {
          const digits = event.target.value.replace(/\D/g, '').slice(0, 5);
          event.target.value = digits.length > 2 ? `${digits.slice(0, 2)}-${digits.slice(2)}` : digits;
        }

        this.clearFieldError(event.target);
        this.markDirty();
        window.clearTimeout(this.saveTimer);
        this.saveTimer = window.setTimeout(() => this.saveAttributes(), 400);
      }

      onChange(event) {
        if (!(event.target instanceof HTMLInputElement) || event.target === this.toggle) return;
        window.clearTimeout(this.saveTimer);
        this.saveAttributes(true);
      }

      onFocusOut(event) {
        if (!this.toggle.checked || !(event.target instanceof HTMLInputElement)) return;
        if (event.target.matches('[data-invoice-required], [data-invoice-email]')) {
          this.validateField(event.target, true);
        }
      }

      onSubmit(event) {
        window.clearTimeout(this.saveTimer);
        if (!this.validateForm(true)) {
          event.preventDefault();
          event.stopPropagation();
          this.focusFirstInvalidField();
        }
      }

      validateForm(showErrors) {
        if (!this.toggle.checked) {
          this.clearAllErrors();
          return true;
        }

        const fields = this.querySelectorAll('[data-invoice-required], [data-invoice-email]');
        let isValid = true;
        fields.forEach((field) => {
          if (!this.validateField(field, showErrors)) isValid = false;
        });
        this.errorSummary.hidden = isValid;
        return isValid;
      }

      validateField(field, showError) {
        let message = '';
        const value = field.value.trim();

        if (field.matches('[data-invoice-required]') && !value) {
          message = `Pole „${field.dataset.invoiceLabel}” jest wymagane.`;
        } else if (field.matches('[data-invoice-tax-id]') && value.replace(/\D/g, '').length !== 10) {
          message = 'NIP musi zawierać dokładnie 10 cyfr.';
        } else if (field.matches('[data-invoice-postal-code]') && !/^\d{2}-\d{3}$/.test(value)) {
          message = 'Wpisz kod pocztowy w formacie XX-XXX.';
        } else if (field.matches('[data-invoice-email]') && value && !field.validity.valid) {
          message = 'Wpisz poprawny adres e-mail.';
        }

        field.setCustomValidity(message);
        if (showError) this.showFieldError(field, message);
        return !message;
      }

      showFieldError(field, message) {
        const error = field.closest('.invoice-form__field')?.querySelector('[data-invoice-error]');
        field.setAttribute('aria-invalid', String(Boolean(message)));
        if (error) error.textContent = message;
      }

      clearFieldError(field) {
        field.setCustomValidity('');
        field.removeAttribute('aria-invalid');
        const error = field.closest('.invoice-form__field')?.querySelector('[data-invoice-error]');
        if (error) error.textContent = '';
        if (this.errorSummary) this.errorSummary.hidden = true;
      }

      clearAllErrors() {
        this.querySelectorAll('input').forEach((field) => this.clearFieldError(field));
        if (this.errorSummary) this.errorSummary.hidden = true;
      }

      focusFirstInvalidField() {
        const firstInvalid = this.querySelector('[aria-invalid="true"]');
        if (firstInvalid) {
          firstInvalid.focus();
          firstInvalid.scrollIntoView({ behavior: 'smooth', block: 'center' });
        } else {
          this.errorSummary?.focus();
        }
      }

      markDirty() {
        this.changeVersion += 1;
        if (this.saveStatus) this.saveStatus.textContent = 'Zapisywanie danych…';
      }

      getAttributes() {
        const formData = new FormData(this.form);
        const attributes = {};

        formData.forEach((value, name) => {
          const match = name.match(/^attributes\[(.+)]$/);
          if (match) attributes[match[1]] = value.toString();
        });

        return attributes;
      }

      saveAttributes() {
        window.clearTimeout(this.saveTimer);
        const version = this.changeVersion;
        const attributes = this.getAttributes();

        this.saveQueue = this.saveQueue
          .catch(() => undefined)
          .then(() =>
            fetch(window.routes.cart_update_url, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                Accept: 'application/json',
              },
              body: JSON.stringify({ attributes }),
              keepalive: true,
            })
          )
          .then((response) => {
            if (!response.ok) throw new Error('Cart attributes could not be saved.');
            this.savedVersion = Math.max(this.savedVersion, version);
            if (this.savedVersion === this.changeVersion && this.saveStatus) {
              this.saveStatus.textContent = 'Dane zapisane w koszyku.';
            }
          })
          .catch(() => {
            if (this.saveStatus) {
              this.saveStatus.textContent = 'Nie udało się zapisać danych. Spróbuj ponownie.';
            }
          });

        return this.saveQueue;
      }

      setupAcceleratedCheckoutGuard() {
        const cartFooter = this.closest('#main-cart-footer');
        const acceleratedCheckout = cartFooter?.querySelector('.additional-checkout-buttons');
        if (!acceleratedCheckout) return;

        acceleratedCheckout.addEventListener(
          'click',
          (event) => {
            if (!this.toggle.checked) return;
            if (!this.validateForm(true) || this.savedVersion < this.changeVersion) {
              event.preventDefault();
              event.stopPropagation();
              this.saveAttributes(true);
              if (!this.validateForm(false)) this.focusFirstInvalidField();
              if (this.saveStatus && this.savedVersion < this.changeVersion) {
                this.saveStatus.textContent = 'Poczekaj na zapisanie danych i kliknij ponownie.';
              }
            }
          },
          true
        );
      }
    }
  );
}
